/* Paint Window */
.paint {
  --shadow-light: rgba(0, 0, 0, 0.15);
  --shadow-medium: rgba(0, 0, 0, 0.2);
  --shadow-heavy: rgba(0, 0, 0, 0.25);
  --shadow-overlay: rgba(0, 0, 0, 0.3);
  --overlay-backdrop: rgba(0, 0, 0, 0.5);
  --color-save-error: #ff6b6b;
  --z-modal: 100;

  position: absolute;
  background: var(--color-surface);
  border: var(--border-medium) var(--border-style) var(--color-border-strong);
  border-radius: var(--radius-window);
  overflow: hidden;
  box-shadow: 0 8px 32px var(--shadow-light);
  will-change: transform;
  display: flex;
  flex-direction: column;
  animation: windowOpen 0.3s ease-out;
  transform-origin: center center;
}

.paint.dragging {
  cursor: grabbing;
  box-shadow: 0 16px 48px var(--shadow-heavy);
}

.paint.maximized {
  border-radius: 0;
}

@keyframes windowOpen {
  from {
    opacity: 0;
    scale: 0.95;
  }
  to {
    opacity: 1;
    scale: 1;
  }
}

.paint.closing {
  animation: windowClose 0.2s ease-in forwards;
  pointer-events: none;
}

@keyframes windowClose {
  from {
    opacity: 1;
    scale: 1;
  }
  to {
    opacity: 0;
    scale: 0.95;
  }
}

.paintHeader {
  background: var(--color-surface-raised);
  border-bottom: var(--border-thin) var(--border-style) var(--color-border);
  padding: var(--space-2) var(--space-3);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.paint.dragging .paintHeader {
  cursor: grabbing;
}

.paintTitle {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
  margin-left: auto;
}

/* Toolbar */
.paintToolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1-5) var(--space-3);
  background: var(--color-surface-sunken);
  border-bottom: var(--border-thin) var(--border-style) var(--color-border);
  flex-wrap: wrap;
}

.toolbarSection {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.toolbarDivider {
  width: var(--border-thin);
  height: var(--space-5);
  background: var(--color-border);
}

/* Color Swatches */
.colorSwatch {
  --swatch-size: 18px;

  width: var(--swatch-size);
  height: var(--swatch-size);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  cursor: pointer;
  padding: 0;
  transition: transform var(--transition-fast);
}

.colorSwatch:hover {
  transform: scale(1.15);
}

.colorSwatch.active {
  border: var(--border-default) var(--border-style) var(--color-text);
  box-shadow: 0 0 0 var(--border-thin) var(--color-surface);
}

/* Stroke Size Buttons */
.strokeButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-6);
  height: var(--space-6);
  padding: 0;
  background: transparent;
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.strokeButton:hover {
  background: var(--color-surface);
}

.strokeButton.active {
  background: var(--color-surface);
  border-color: var(--color-text);
}

.strokeDot {
  border-radius: var(--radius-full);
  background: var(--color-text);
}

/* Eraser Swatch */
.eraserSwatch {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
}

.eraserSwatch:hover {
  transform: scale(1.15);
  color: var(--color-text);
}

/* Toolbar Actions */
.toolbarActions {
  display: flex;
  gap: var(--space-1);
  margin-left: auto;
}

/* Clear Button */
.clearButton {
  padding: var(--space-1) var(--space-2);
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
  background: transparent;
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.clearButton:hover {
  background: var(--color-surface);
  color: var(--color-text);
}

/* Save Dialog */
.saveDialog {
  position: absolute;
  inset: 0;
  background: var(--overlay-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
}

.saveDialogContent {
  background: var(--color-surface);
  border: var(--border-medium) var(--border-style) var(--color-border-strong);
  border-radius: var(--radius-sm);
  padding: var(--space-4);
  min-width: 280px;
  box-shadow: 0 8px 32px var(--shadow-overlay);
}

.saveDialogTitle {
  font-family: var(--font-mono);
  font-size: var(--body-size);
  font-weight: var(--font-semibold);
  color: var(--color-text);
  margin-bottom: var(--space-3);
}

.saveDialogField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-2);
}

.saveDialogLabel {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-muted);
}

.saveDialogInput {
  padding: var(--space-2);
  font-family: var(--font-mono);
  font-size: var(--body-size);
  color: var(--color-text);
  background: var(--color-surface-sunken);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  outline: none;
}

.saveDialogInput:focus {
  border-color: var(--color-accent-primary);
}

.saveDialogInput::placeholder {
  color: var(--color-text-subtle);
}

.saveDialogLocation {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text-subtle);
  margin-bottom: var(--space-3);
}

.saveDialogError {
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-error, var(--color-save-error));
  margin-bottom: var(--space-2);
}

.saveDialogButtons {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.saveDialogButton {
  padding: var(--space-1) var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text);
  background: var(--color-surface-sunken);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.saveDialogButton:hover {
  background: var(--color-surface-raised);
}

.saveDialogButton.primary {
  background: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: white;
}

.saveDialogButton.primary:hover {
  opacity: 0.9;
}

/* Clip Warning */
.clipWarning {
  position: absolute;
  top: var(--space-2);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  font-family: var(--font-mono);
  font-size: var(--caption-size);
  color: var(--color-text);
  background: var(--color-surface);
  border: var(--border-thin) var(--border-style) var(--color-border);
  border-radius: var(--radius-xs);
  box-shadow: 0 2px 8px var(--shadow-medium);
}

.clipWarningClose {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-0-5, 2px);
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.clipWarningClose:hover {
  color: var(--color-text);
  background: var(--color-surface-sunken);
}

/* Canvas Area */
.paintCanvasWrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.paintCanvas {
  display: block;
  cursor: crosshair;
  touch-action: none;
}
